import React, { useState } from "react";
import { Navbar } from "@/components/Navbar";
import { Timeline } from "@/components/Timeline";
import { StatusBadge } from "@/components/StatusBadge";
import { ReceiptModal } from "@/components/ReceiptModal";
import { LiveOperations } from "@/components/LiveOperations";
import { Footer } from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PUBLIC_TRACK } from "@/constants/testIds";
import { Search, Loader2, FileText, MapPin, PackageX } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1494412519320-aa613dfb7738?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";
const SAMPLES_HINT = "Try a tracking number shared by Go Deliver (format GD-2026-XXXXXX)";

export default function TrackingPage() {
  const [tn, setTn] = useState("");
  const [result, setResult] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showReceipt, setShowReceipt] = useState(false);
  const [samples, setSamples] = useState([]);

  React.useEffect(() => {
    api
      .get("/track-samples/list")
      .then(({ data }) => setSamples(data || []))
      .catch(() => setSamples([]));
  }, []);

  const doTrack = async (value) => {
    const q = (value ?? tn).trim();
    if (!q) return;
    setLoading(true);
    setError("");
    setNotFound(false);
    setResult(null);
    try {
      const { data } = await api.get(`/track/${encodeURIComponent(q)}`);
      setResult(data);
    } catch (err) {
      if (err.response?.status === 404) setNotFound(true);
      else setError(formatApiErrorDetail(err.response?.data?.detail) || "Unable to track right now");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    doTrack();
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/90 via-slate-950/85 to-[#0B0F17]" />
        <div className="relative mx-auto max-w-4xl px-4 py-20 text-center sm:py-28">
          <p className="mb-3 text-xs font-mono uppercase tracking-[0.25em] text-orange-400">
            Freight & Parcel Tracking
          </p>
          <h1 className="text-4xl font-black tracking-tight sm:text-5xl lg:text-6xl">
            Track every move.<br />
            <span className="text-orange-500">Zero guesswork.</span>
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-base text-slate-300">
            Enter your Go Deliver tracking number to see live status, route and a full event timeline.
          </p>

          <form onSubmit={onSubmit} className="mx-auto mt-8 flex max-w-xl flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid={PUBLIC_TRACK.trackingInput}
                value={tn}
                onChange={(e) => setTn(e.target.value.toUpperCase())}
                placeholder="GD-2026-XXXXXX"
                className="h-14 border-white/15 bg-slate-900/80 pl-12 font-mono text-lg uppercase tracking-wider text-white placeholder:text-slate-400"
              />
            </div>
            <Button
              data-testid={PUBLIC_TRACK.trackButton}
              type="submit"
              disabled={loading}
              className="h-14 bg-orange-600 px-8 text-base font-bold text-white hover:bg-orange-700 active:scale-[0.98]"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Track"}
            </Button>
          </form>
          <p className="mt-3 text-xs text-slate-500">{SAMPLES_HINT}</p>
          {samples.length > 0 && (
            <div className="mt-3 flex flex-wrap justify-center gap-2">
              {samples.map((s, i) => (
                <button
                  key={s}
                  data-testid={`${PUBLIC_TRACK.samplePill}${i}`}
                  onClick={() => { setTn(s); doTrack(s); }}
                  className="rounded-full border border-white/15 bg-white/5 px-3 py-1 font-mono text-xs text-slate-300 hover:border-orange-500/60 hover:text-orange-300"
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Result */}
      <section className="mx-auto max-w-4xl px-4 pb-24">
        {error && (
          <div className="rounded-xl border border-rose-600/50 bg-rose-950/50 p-4 text-rose-300">{error}</div>
        )}

        {notFound && (
          <div data-testid={PUBLIC_TRACK.notFound} className="flex flex-col items-center gap-3 rounded-2xl border border-white/10 bg-slate-900/60 p-12 text-center">
            <PackageX className="h-10 w-10 text-slate-500" />
            <p className="text-lg font-bold">No shipment found</p>
            <p className="text-sm text-slate-400">We couldn't find any shipment for that tracking number. Double-check and try again.</p>
          </div>
        )}

        {result && (
          <div data-testid={PUBLIC_TRACK.resultContainer} className="overflow-hidden rounded-2xl border border-white/10 bg-slate-900/70">
            <div className="flex flex-col gap-4 border-b border-white/10 p-6 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="font-mono text-xs uppercase tracking-wider text-slate-400">Tracking Number</p>
                <p className="font-mono text-2xl font-bold text-white">{result.tracking_number}</p>
                <div className="mt-2 flex items-center gap-2 text-sm text-slate-300">
                  <MapPin className="h-4 w-4 text-orange-400" />
                  {result.origin} <span className="text-slate-600">→</span> {result.destination}
                </div>
              </div>
              <div className="flex flex-col items-start gap-3 sm:items-end">
                <StatusBadge status={result.status} />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowReceipt(true)}
                  className="border-white/15 bg-transparent text-slate-200 hover:bg-white/10"
                >
                  <FileText className="mr-1 h-4 w-4" /> View Receipt
                </Button>
              </div>
            </div>
            <div className="p-6">
              <Timeline status={result.status} history={result.timeline} />
            </div>
          </div>
        )}
      </section>

      <LiveOperations />
      <Footer />

      <ReceiptModal open={showReceipt} onOpenChange={setShowReceipt} shipment={result} />
    </div>
  );
}
